'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface InviteMemberRequest {
  email: string;
  role: 'admin' | 'member';
}

interface InviteMemberResponse {
  message: string;
  userId: string;
  email: string;
  role: string;
}

interface InviteMemberError {
  status: number;
  message: string;
}

/**
 * Hook for inviting a new member to the organization
 * POST /api/v1/organization/invitations
 *
 * Returns 201 on success
 * Returns 402 when at seat limit (upgrade required)
 * Returns 403 when over seat limit
 */
export function useInviteMember() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    InviteMemberResponse,
    InviteMemberError,
    InviteMemberRequest
  >({
    mutationFn: async ({ email, role }) => {
      const token = await getToken();
      if (!token) {
        throw { status: 401, message: 'No authentication token available' };
      }

      const { data, error, response } = await apiClient.POST(
        '/api/v1/organization/invitations',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            email,
            role,
          },
        }
      );

      if (response.status === 402) {
        throw {
          status: 402,
          message:
            'Seat limit reached. Upgrade your subscription to add more members.',
        };
      }

      if (response.status === 403) {
        throw {
          status: 403,
          message: 'Over seat limit. Cannot add more members.',
        };
      }

      if (error || !response.ok || !data) {
        throw { status: response.status, message: 'Failed to invite member' };
      }

      return {
        message: data.message,
        userId: data.userId,
        email: data.email,
        role: data.role,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      // The invite request persists the pending invitation row synchronously, so refetch
      // the invitations list now — the row's Resend/Revoke (keyed on the invitation id)
      // appear immediately instead of waiting on the fastPoll tick.
      queryClient.invalidateQueries({ queryKey: ['organization-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
